import themeColors, { themeFonts, themeFontsSize, themeFontWeights } from "@GeriMed/styles/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getJsonLdScriptId from "@insite/client-framework/Common/Utilities/getJsonLdScriptId";
import prepareBreadcrumbStructuredPageData from "@insite/client-framework/Common/Utilities/prepareBreadcrumbStructuredPageData";
import setPageMetadata from "@insite/client-framework/Common/Utilities/setPageMetadata";
import { trackPageChange } from "@insite/client-framework/Common/Utilities/tracking";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setBreadcrumbs from "@insite/client-framework/Store/Components/Breadcrumbs/Handlers/SetBreadcrumbs";
import {
    getSelectedCategoryPath,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import CurrentCategory from "@insite/content-library/Components/CurrentCategory";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Page from "@insite/mobius/Page";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface State {
    metadataSetForId?: string;
}


const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const categoryPath =
        getSelectedCategoryPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const catalogPageState = getCatalogPageStateByPath(state, categoryPath);

    return {
        catalogPageState,
        category: getCategoryState(
            state,
            catalogPageState.value?.categoryIdWithBrandId ?? catalogPageState.value?.categoryId,
        ).value,
        location,
        websiteName: state.context.website.name,
        websiteSettings: getSettingsCollection(state).websiteSettings,
        breadcrumbLinks: state.components.breadcrumbs.links,
        page: getCurrentPage(state),
    };
};

const mapDispatchToProps = {
    setBreadcrumbs,
};

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const customCssWrapper = {
    css: css`
        &.category-wrapper{
            padding: 0 15px;
        }
        .category-name {
            margin: 0 0 15px;
            font-weight: ${themeFontWeights.semiBold};
            line-height: ${themeFontsSize.size28};
        }

        .sub-category{
            a span {
                line-height: ${themeFontsSize.size18};
                font-size: ${themeFontsSize.size14};
                font-family: ${themeFonts.fontFamilyProximaNova};
            }

            &:last-child{
                padding-bottom:0;
            }
        } 

        .cat-list {
            align-items: center;
        }
        @media screen and (min-width: 768px) {
            .category-page > div > div:first-child {
                max-width: 250px;
                padding: 1.2em;
                margin: 0;
                margin-bottom: 3em;
                background: ${themeColors.tableOddcellBgcolor};
                height: fit-content;
                border: 1px solid #e1e1e1;
            }
            .category-page > div > div:nth-child(2){
                flex-basis: 78.5%;
                max-width: 78.5%;
            }
            .cat-lists {
                width: 90%;
                float: right;
                margin: 24px 0;
            }
        }
        @media screen and (max-width: 767px) {
            .category-page > [class*="GridOffset"] {
                display:block;
            }
    
            .category-page > div > div:first-child {
                max-width: 100%;
                text-align: center;
                justify-content: center;
            }
            .sub-category {
                justify-content: center;

                a span {
                    line-height: ${themeFontsSize.size20};
                    font-size: ${themeFontsSize.size16};
                }
            }
            .category-page > div > div:nth-child(2){
                flex-basis: 100%;
                max-width: 100%;
            }
            .cat-lists {
                margin: 24px 0;
            }
        }

        @media screen and (min-width: 768px) and (max-width: 1192px) {
            .category-page > div > div:nth-child(2) {
                flex-basis: 75%;
                max-width: 75%;
            }
        }
    `,
};

class CategoryDetailsPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {};
    }

    UNSAFE_componentWillMount() {
        this.setMetadata();
        if (this.props.catalogPageState.value) {
            const breadcrumbLinks = this.props.breadcrumbLinks;
            if (
                breadcrumbLinks
                    ?.slice(1)
                    ?.map(o => o.children?.toString())
                    .join(",") !==
                    this.props.catalogPageState.value.breadCrumbs
                        ?.slice(1)
                        ?.map(o => o.text)
                        .join(",") ||
                (breadcrumbLinks?.length && breadcrumbLinks[0].children !== translate("Home_Breadcrumb"))
            ) {
                this.props.setBreadcrumbs({
                    links: this.props.catalogPageState.value!.breadCrumbs!.map(o => ({
                        children: o.text,
                        href: o.url,
                    })),
                });
            }
        }
    }

    componentDidUpdate() {
        const { category } = this.props;
        if (category && category.id !== this.state.metadataSetForId) {
            trackPageChange();
            this.setMetadata();
        }
    }

    setMetadata() {
        const { catalogPageState, category, websiteName, location, websiteSettings } = this.props;
        if (!catalogPageState?.value || !category || !this.props.breadcrumbLinks) {
            return;
        }

        const { metaDescription, metaKeywords, openGraphImage, openGraphTitle, openGraphUrl, title } =
            catalogPageState.value;

        setPageMetadata(
            {
                metaDescription,
                metaKeywords,
                openGraphImage,
                openGraphTitle,
                openGraphUrl,
                currentPath: location.pathname,
                canonicalPath: catalogPageState.value.canonicalPath,
                alternateLanguageUrls: catalogPageState.value.alternateLanguageUrls ?? undefined,
                title: title || category.name,
                websiteName,
                structuredPageData: websiteSettings.enableBreadcrumbStructuredData
                    ? prepareBreadcrumbStructuredPageData(this.props.breadcrumbLinks)
                    : undefined,
                jsonLdScriptId: getJsonLdScriptId(this.props.page.type),
            },
            websiteSettings,
        );

        this.setState({
            metadataSetForId: category.id,
        });
    }
    render() {
        return (
            <Page className="category-wrapper" {...customCssWrapper}>
                <CurrentCategory>
                    <StyledWrapper>
                        <Zone contentId={this.props.id} zoneName="Content" />
                    </StyledWrapper>
                </CurrentCategory>
            </Page>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CategoryDetailsPage),
    definition: {
        hasEditableUrlSegment: false,
        hasEditableTitle: true,
        supportsCategorySelection: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "CategoryDetailsPage" instead of this constant.
 */
export const CategoryDetailsPageContext = "CategoryDetailsPage";
